import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getRoomCategoryByResortId } from "../../../../../../../apis/property";
import { useParams } from "react-router-dom";
import {
  XIcon,
  HomeIcon,
  TagIcon,
  DocumentTextIcon,
  UsersIcon,
  CollectionIcon,
} from "@heroicons/react/outline";

const CreateRoomType = ({ isOpen, onClose }) => {
  const { id } = useParams();
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    description: "",
    total_count: "",
    min_occupancy: "",
    max_occupancy: "",
    room_category_id: "",
    resort_id: id,
  });
  const [roomCategory, setRoomCategory] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const fetchRoomCategory = async () => {
    try {
      const response = await getRoomCategoryByResortId(id);
      setRoomCategory(response.data);
    } catch (error) {
      console.log("Error fetching room categories:", error);
    }
  };

  // The modal loads its option list once when it mounts.
  useEffect(() => {
    fetchRoomCategory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateForm = () => {
    const { name, code, total_count, min_occupancy, max_occupancy, room_category_id } = formData;
    if (name.length < 3 || name.length > 50) {
      toast.error("Room type name must be between 3 and 50 characters.");
      return false;
    }
    if (code.length < 2 || code.length > 10) {
      toast.error("Room type code must be between 2 and 10 characters.");
      return false;
    }
    if (!/^\d+$/.test(total_count) || parseInt(total_count) <= 0) {
      toast.error("Total number must be a positive integer.");
      return false;
    }
    if (!/^\d+$/.test(min_occupancy) || parseInt(min_occupancy) < 1) {
      toast.error("Minimum occupancy must be at least 1.");
      return false;
    }
    if (!/^\d+$/.test(max_occupancy) || parseInt(max_occupancy) < parseInt(min_occupancy)) {
      toast.error("Maximum occupancy must be greater than or equal to minimum occupancy.");
      return false;
    }
    if (!room_category_id) {
      toast.error("Please select a room category.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);

    try {
      const accessToken = localStorage.getItem("accessToken");
      const response = await fetch(`${baseUrl}/resort/room/type`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success("Room Type created successfully");
        setFormData({
          name: "",
          code: "",
          description: "",
          total_count: "",
          min_occupancy: "",
          max_occupancy: "",
          room_category_id: "",
          resort_id: id,
        });
        onClose();
      } else {
        toast.error("Failed to create Room Type");
      }
    } catch (error) {
      toast.error("Error occurred while creating Room Type");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity" onClick={onClose}></div>
        <div className="flex min-h-screen items-center justify-center p-4">
          <div className="relative w-full max-w-2xl transform transition-all">
            <div className="modal-container">
              <div className="modal-header">
                <div className="flex items-center space-x-3">
                  <div className="modal-icon">
                    <HomeIcon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="modal-title">Add Room Type</h3>
                    <p className="modal-subtitle">Create a new room type configuration</p>
                  </div>
                </div>
                <button onClick={onClose} className="modal-close-btn" type="button">
                  <XIcon className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="form-group md:col-span-2">
                      <label htmlFor="name" className="form-label">
                        <HomeIcon className="form-label-icon" />
                        Room Type Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="e.g., Deluxe Suite"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="code" className="form-label">
                        <TagIcon className="form-label-icon" />
                        Room Type Code *
                      </label>
                      <input
                        type="text"
                        id="code"
                        name="code"
                        value={formData.code}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="e.g., DLX"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="total_count" className="form-label">
                        <CollectionIcon className="form-label-icon" />
                        Total Count *
                      </label>
                      <input
                        type="number"
                        id="total_count"
                        name="total_count"
                        value={formData.total_count}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="e.g., 10"
                        min="1"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="min_occupancy" className="form-label">
                        <UsersIcon className="form-label-icon" />
                        Min Occupancy *
                      </label>
                      <input
                        type="number"
                        id="min_occupancy"
                        name="min_occupancy"
                        value={formData.min_occupancy}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="e.g., 1"
                        min="1"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="max_occupancy" className="form-label">
                        <UsersIcon className="form-label-icon" />
                        Max Occupancy *
                      </label>
                      <input
                        type="number"
                        id="max_occupancy"
                        name="max_occupancy"
                        value={formData.max_occupancy}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="e.g., 4"
                        min="1"
                        required
                      />
                    </div>

                    <div className="form-group md:col-span-2">
                      <label htmlFor="description" className="form-label">
                        <DocumentTextIcon className="form-label-icon" />
                        Description *
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        className="form-textarea"
                        placeholder="Describe the room type..."
                        rows="4"
                        required
                      />
                    </div>

                    <div className="form-group md:col-span-2">
                      <label htmlFor="room_category_id" className="form-label">
                        <CollectionIcon className="form-label-icon" />
                        Room Category *
                      </label>
                      <select
                        id="room_category_id"
                        name="room_category_id"
                        value={formData.room_category_id}
                        onChange={handleChange}
                        className="form-select"
                        required
                      >
                        <option value="">Select a category</option>
                        {roomCategory?.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button type="button" onClick={onClose} className="btn-secondary" disabled={isSubmitting}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Creating...
                      </>
                    ) : (
                      'Add Room Type'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .modal-container {
          background: white;
          border-radius: 1rem;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          overflow: hidden;
        }

        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1.5rem;
          background: linear-gradient(135deg, #3b82f6, #2563eb);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .modal-icon {
          width: 3rem;
          height: 3rem;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border-radius: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .modal-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: white;
          margin: 0;
        }

        .modal-subtitle {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.9);
          margin: 0.25rem 0 0 0;
        }

        .modal-close-btn {
          padding: 0.5rem;
          background: rgba(255, 255, 255, 0.2);
          border: none;
          border-radius: 0.5rem;
          color: white;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .modal-close-btn:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: scale(1.1);
        }

        .modal-body {
          padding: 2rem;
          max-height: 60vh;
          overflow-y: auto;
        }

        .modal-body::-webkit-scrollbar {
          width: 8px;
        }

        .modal-body::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }

        .modal-body::-webkit-scrollbar-thumb {
          background: #3b82f6;
          border-radius: 4px;
        }

        .form-group {
          margin-bottom: 0;
        }

        .form-label {
          display: flex;
          align-items: center;
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
          margin-bottom: 0.5rem;
        }

        .form-label-icon {
          width: 1rem;
          height: 1rem;
          margin-right: 0.5rem;
          color: #3b82f6;
        }

        .form-input,
        .form-textarea,
        .form-select {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 2px solid #e5e7eb;
          border-radius: 0.75rem;
          font-size: 0.875rem;
          transition: all 0.2s ease;
          background: #f9fafb;
        }

        .form-input:focus,
        .form-textarea:focus,
        .form-select:focus {
          outline: none;
          border-color: #3b82f6;
          background: white;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .form-textarea {
          resize: vertical;
          min-height: 100px;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          padding: 1.5rem;
          background: #f9fafb;
          border-top: 1px solid #e5e7eb;
        }

        .btn-secondary {
          padding: 0.75rem 1.5rem;
          background: white;
          border: 2px solid #e5e7eb;
          border-radius: 0.75rem;
          font-weight: 600;
          color: #374151;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .btn-secondary:hover:not(:disabled) {
          background: #f3f4f6;
          border-color: #d1d5db;
        }

        .btn-secondary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .btn-primary {
          padding: 0.75rem 1.5rem;
          background: linear-gradient(135deg, #3b82f6, #2563eb);
          border: none;
          border-radius: 0.75rem;
          font-weight: 600;
          color: white;
          cursor: pointer;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #2563eb, #1d4ed8);
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(59, 130, 246, 0.4);
        }

        .btn-primary:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
      `}</style>
    </>
  );
};

export default CreateRoomType;
